/**
 * Number Formatting Utilities
 * Provides functions for consistent formatting of numbers throughout the app
 */

/**
 * Format a number with thousand separators
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted number with commas
 *
 * Examples:
 * formatNumber(1234.56) → "1,234.56"
 * formatNumber(1000000) → "1,000,000.00"
 * formatNumber(42.5) → "42.50"
 */
export function formatNumber(value, decimals = 2) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0.00';
  }

  const number = parseFloat(value);
  return number.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

/**
 * Format a number as currency with the appropriate currency symbol
 * @param {number} value - The amount to format
 * @param {string} currencyCode - ISO 4217 currency code (default: 'SGD')
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted currency string
 *
 * Examples:
 * formatCurrency(1234.56, 'SGD') → "S$1,234.56"
 * formatCurrency(1234.56, 'IDR', 0) → "Rp 1,235"
 * formatCurrency(1234.56, 'USD') → "$1,234.56"
 */
export function formatCurrency(value, currencyCode = 'SGD', decimals) {
  const code = currencyCode || 'SGD';

  // For IDR, default to 0 decimal places since amounts are large whole numbers
  const dec = decimals !== undefined ? decimals : (code === 'IDR' ? 0 : 2);

  if (value === null || value === undefined || isNaN(value)) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: code, minimumFractionDigits: dec, maximumFractionDigits: dec }).format(0);
    } catch {
      return '$0.00';
    }
  }

  const number = parseFloat(value);
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: dec,
      maximumFractionDigits: dec,
    }).format(number);
  } catch {
    return '$' + number.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

/**
 * Format a large number with abbreviated notation (K, M, B)
 * @param {number} value - The number to format
 * @returns {string} Abbreviated number
 *
 * Examples:
 * formatCompact(1234) → "1.2K"
 * formatCompact(1234567) → "1.2M"
 * formatCompact(1234567890) → "1.2B"
 */
export function formatCompact(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }

  const number = parseFloat(value);
  const abs = Math.abs(number);

  if (abs >= 1e9) {
    return (number / 1e9).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'B';
  }
  if (abs >= 1e6) {
    return (number / 1e6).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'M';
  }
  if (abs >= 1e3) {
    return (number / 1e3).toLocaleString('en-US', { maximumFractionDigits: 1 }) + 'K';
  }

  return number.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/**
 * Format a percentage with optional decimals
 * @param {number} value - The percentage value (0-100)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted percentage
 *
 * Examples:
 * formatPercent(75.5) → "75.5%"
 * formatPercent(33.333333) → "33.3%"
 */
export function formatPercent(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }

  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }) + '%';
}
